import axios from 'axios';
import { getAuthToken, clearAuthToken } from './auth-token';
import { getApiUrl } from './env';
import { toCamelCase, toSnakeCase } from './api-transform';

export { toSnakeCase, toCamelCase } from './api-transform';

export const api = axios.create({
  baseURL: getApiUrl(),
  withCredentials: true,
});

function serializeRequestBody(body?: unknown): unknown {
  if (body instanceof FormData) {
    return body;
  }
  return toCamelCase(body);
}

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  } else if (!config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuthToken();
    }
    return Promise.reject(error);
  },
);

export async function apiGet<T>(path: string): Promise<T> {
  const { data } = await api.get(path);
  return toSnakeCase<T>(data);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const { data } = await api.post(path, serializeRequestBody(body));
  return toSnakeCase<T>(data);
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const { data } = await api.put(path, serializeRequestBody(body));
  return toSnakeCase<T>(data);
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const { data } = await api.patch(path, serializeRequestBody(body));
  return toSnakeCase<T>(data);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const { data } = await api.delete(path);
  return toSnakeCase<T>(data);
}

export async function uploadFile(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  return apiPost<{ url: string }>('/upload', formData);
}
